import React, { useState, useEffect, useContext } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";

import { useMutation, useQuery } from "@apollo/client";
import toast from "react-hot-toast";

import Filter from './components/ClaimsFilter'
import claimsGQL from '../../gql/claims'
import gqlPreAuth from '../../gql/preAuth'
import { appContext } from '../../context/ContextProvider'
import Pagination from '../../components/Pagination'
import './styles/pre-auth.css'
import '../Beneficiary/styles/BeneficiaryList.css'
import AppErrorAlert from '../../components/AppErrorAlert'
import ClaimJsonViewModal from './ClaimJsonViewModal'
import { Alert, Badge, Tooltip, Box, Skeleton } from '@mui/material'
import { MdArrowCircleUp, MdOutlineRemoveRedEye } from 'react-icons/md'
import { IoMdCloseCircleOutline } from 'react-icons/io'
import { TbEditCircle } from 'react-icons/tb'
import { BsFiletypeJson } from 'react-icons/bs'
import ClaimReportModal from './components/ClaimReportModal'
import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import AutoModeIcon from '@mui/icons-material/AutoMode'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'

import { styled, alpha } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FileUpload, HistoryOutlined } from "@mui/icons-material";
import { RiMessage2Line } from "react-icons/ri";
import ConformationModal from "../../components/ConformationModal";
import { useTranslation } from "react-i18next";
import ClaimUploadListSkeleton from "../../common/ClaimUploadListSkeleton";
import TableSkeletonLoader from "../../common/TableLoading";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import AppButton from "../../shared/AppButton";
import AppTable from "../../shared/AppTable";
import StatusOutcomePill from "../../shared/StatusOutcomePill";
import AppModal from "../../components/AppModal";
import PollIcon from "@mui/icons-material/Poll";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import BulkCancelBundleModal from "./components/BulkCancelBundleModal";
import InvalidICDCodeModal from "./components/InvalidICDCodeModal";
import ClaimItemIssueCodesModal from "./components/ClaimItemIssueCodesModal";
import {
  isEditLockedStatus,
  normalizeTransactionStatus,
} from "../../utils/transactionStatus";
import ReferralFlag from "../../components/ReferralFlag";

const DEFAULT_LIMIT = 15;

const readStorageJson = (storage, key) => {
  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (e) {
    console.error(`Failed to parse ${key}`, e);
    return null;
  }
};

const canEditClaimTransaction = (status) =>
  normalizeTransactionStatus(status) === "pending" ||
  !isEditLockedStatus(status);

const getPreviousMonthBatchDates = () => {
  const today = new Date()
  const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const endDate = new Date(today.getFullYear(), today.getMonth(), 0)
  const formatDate = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  return {
    batch_start_date: formatDate(startDate),
    batch_end_date: formatDate(endDate)
  }
}

function ClaimsList(props) {
  const { t, i18n } = useTranslation();
  const { setShowLoader, setShowErrorAlert } = useContext(appContext);
  const navigate = useNavigate();
  const location = useLocation();
  const currentDate = new Date();
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(() => {
    const saved = sessionStorage.getItem("claimsShowFilters");
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    sessionStorage.setItem("claimsShowFilters", JSON.stringify(showFilters));
  }, [showFilters]);

  const [excelData, setExcelData] = useState([]);
  const [errorData, setErrorData] = useState([]);
  const [allClaims, setAllClaims] = useState([]);
  const [claimsTotal, setClaimsTotal] = useState(0);
  const [claimsPages, setClaimsPages] = useState(0);

  const [reportOpen, setReportOpen] = useState(false);
  const handleReportOpen = () => setReportOpen(true);
  const handleReportClose = () => setReportOpen(false);

  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem("claimsActiveTab");
    if (saved !== null) return JSON.parse(saved);
    return readStorageJson(localStorage, "claimsFiltersSaved")?.outcome || "";
  });

  useEffect(() => {
    sessionStorage.setItem("claimsActiveTab", JSON.stringify(activeTab));
  }, [activeTab]);

  const [item, setItem] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);
  const [showBulkReadyModal, setShowBulkReadyModal] = useState(false);

  // Stats and Error List States
  const [stats, setStats] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showInvalidICDModal, setShowInvalidICDModal] = useState(false);
  const [showClaimItemIssueModal, setShowClaimItemIssueModal] = useState(false);

  const fromDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-01`;

  const toDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-${new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  ).getDate()}`;

  const [limit, setLimit] = useState(() => {
    return (
      readStorageJson(localStorage, "claimsListPagination")?.limit ||
      DEFAULT_LIMIT
    );
  });
  const [pageNo, setPageNo] = useState(() => {
    return readStorageJson(localStorage, "claimsListPagination")?.pageNo || 1;
  });

  const [claimsFilters, setClaimsFilters] = useState(() => {
    const saved =
      readStorageJson(localStorage, "claimsFiltersSaved") ||
      readStorageJson(sessionStorage, "claimsFiltersSaved");
    const savedPagination = readStorageJson(
      localStorage,
      "claimsListPagination",
    );
    const defaultFilters = {
      from_date: fromDate,
      to_date: toDate,
      limit: savedPagination?.limit || DEFAULT_LIMIT,
      page: 1,
      payer_id: "",
      tpa_id: "",
      type: "",
      patient_id: "",
      batch_id: "",
      tp_ref_no: "",
      ...(activeTab && { outcome: activeTab }),
      from_date_ordered: "",
      to_date_ordered: "",
      status: "",
      request_bundle_id: "",
      is_followup_req: "",
    };

    if (saved) {
      const hydratedFilters = {
        ...defaultFilters,
        ...saved,
        limit: savedPagination?.limit || saved.limit || DEFAULT_LIMIT,
        page: savedPagination?.pageNo || saved.page || 1,
      };

      const normalizedFilters = {
        ...hydratedFilters,
        ...(activeTab && { outcome: activeTab }),
      };

      if (!activeTab) delete normalizedFilters.outcome;

      return removeEmptyKeys(normalizedFilters);
    }

    return defaultFilters;
  });

  useEffect(() => {
    const cleanedFilters = removeEmptyKeys({
      ...claimsFilters,
      ...(activeTab && { outcome: activeTab }),
    });
    if (!activeTab) delete cleanedFilters.outcome;
    sessionStorage.setItem(
      "claimsFiltersSaved",
      JSON.stringify(cleanedFilters),
    );
    localStorage.setItem("claimsFiltersSaved", JSON.stringify(cleanedFilters));
  }, [claimsFilters, activeTab]);

  const {
    loading: dataLoading,
    error,
    data,
    refetch,
  } = useQuery(claimsGQL.GET_CLAIMS_BY_FILTER, {
    variables: {
      input: getCleanClaimsFilters(),
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
  });

  useEffect(() => {
    if (location.state?.claimsFilters) {
      const nextFilters = removeEmptyKeys({
        ...location.state.claimsFilters,
        ...(location.state.activeTab && { outcome: location.state.activeTab }),
      });
      if (!location.state.activeTab) delete nextFilters.outcome;

      setClaimsFilters(nextFilters);
      setPageNo(location.state.claimsFilters.page || 1);
      setLimit(location.state.claimsFilters.limit || DEFAULT_LIMIT);
      setActiveTab(location.state.activeTab || "");
      refetch({ input: nextFilters });
      return;
    }

    if (location.state?.refreshAt) {
      refetch({ input: getCleanClaimsFilters() });
    }
  }, [location.state?.refreshAt, location.state?.claimsFilters]);

  useEffect(() => {
    if (data?.listClaimTransactions) {
      setAllClaims(data?.listClaimTransactions?.data || []);
      setClaimsPages(data?.listClaimTransactions?.pages || 0);
      setClaimsTotal(data?.listClaimTransactions?.total || 0);
      setStats(data?.listClaimTransactions?.stats || null);
    } else {
      setAllClaims([]);
      setStats(null);
    }
  }, [data]);

  const handleStatErrorClick = (errorType, errorItem) => {
    const typeMapping = {
      error: "error",
      abnormal: "Abnormal Request",
      exception_error: "Exception Error",
      validation_error: "Validation Error",
    };

    const input = {
      from_date: claimsFilters.from_date,
      to_date: claimsFilters.to_date,
      tpa_id: claimsFilters.tpa_id,
      outcome: typeMapping[errorType] || errorType,
      error_value: errorItem.error_code,
      error: errorItem.error_message,
      limit: 50,
      page: 1,
    };

    navigate("/claims/error-list", {
      state: {
        filterInput: input,
        errorDetails: errorItem,
        claimsListReturnState: {
          claimsFilters: getCleanClaimsFilters(),
          activeTab,
        },
      },
    });
  };

  const handleDispositionGroupClick = (group) => {
    if (!group.dispositionValue) return;

    const input = {
      from_date: claimsFilters.from_date,
      to_date: claimsFilters.to_date,
      tpa_id: claimsFilters.tpa_id,
      outcome: "error",
      disposition: group.dispositionValue,
      limit: 50,
      page: 1,
    };

    navigate("/claims/error-list", {
      state: {
        filterInput: input,
        errorDetails: {
          mode: "disposition",
          disposition: group.disposition,
          dispositionValue: group.dispositionValue,
          count: group.count,
          total_net: group.totalNet,
        },
        claimsListReturnState: {
          claimsFilters: getCleanClaimsFilters(),
          activeTab,
        },
      },
    });
  };

  useEffect(() => {
    if (error?.graphQLErrors[0]?.message) {
      toast.error(error?.graphQLErrors[0]?.message);
    }
  }, [error]);

  const [open, setOpen] = useState(false);
  const [selectedItem, setSeelectedItem] = useState(null);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const formatDateTime = (time) => {
    if (!time) return "";
    const sanitizedTime = time.replace(/\s\+\d{4}$/, "");
    const dateTime = new Date(sanitizedTime);
    if (isNaN(dateTime)) return "Invalid date";
    const dateOnly = dateTime.toLocaleDateString("en-GB");
    const timeOnly = dateTime.toLocaleTimeString(navigator.language, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateOnly} ${timeOnly}`;
  };

  function removeEmptyKeys(obj) {
    const newObj = {};
    for (const key in obj) {
      if (
        obj[key] !== "" &&
        obj[key] !== null &&
        obj[key] !== undefined &&
        !(key === "is_followup_req" && obj[key] === false)
      ) {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  }

  function getCleanClaimsFilters(
    baseFilters = claimsFilters,
    tab = activeTab,
    overrides = {},
  ) {
    const cleanedFilters = removeEmptyKeys({
      ...baseFilters,
      ...overrides,
      ...(tab && { outcome: tab }),
    });

    if (!tab) delete cleanedFilters.outcome;

    return cleanedFilters;
  }

  function FormatDate(date) {
    const options = { year: "numeric", month: "numeric", day: "numeric" };
    return new Intl.DateTimeFormat("en-GB", options).format(new Date(date));
  }

  const formatAmount = (value) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);

  const netSummaryItems = [
    {
      label: t("Pending Net"),
      value: stats?.pending_total_net,
      accentClassName: "bg-amber-400",
      textClassName: "text-amber-700",
    },
    {
      label: t("Pended Net"),
      value: stats?.pended_total_net,
      accentClassName: "bg-sky-500",
      textClassName: "text-sky-700",
    },
    {
      label: t("Error Net"),
      value: stats?.error_total_net,
      accentClassName: "bg-rose-500",
      textClassName: "text-rose-700",
    },
    {
      label: t("Abnormal Net"),
      value: stats?.abnormal_total_net,
      accentClassName: "bg-orange-500",
      textClassName: "text-orange-700",
    },
    {
      label: t("Exception Net"),
      value: stats?.exception_error_total_net,
      accentClassName: "bg-violet-500",
      textClassName: "text-violet-700",
    },
    {
      label: t("Validation Net"),
      value: stats?.validation_error_total_net,
      accentClassName: "bg-[#0070C2]",
      textClassName: "text-[#0070C2]",
    },
  ];
  const showStatsSkeleton = (loading || dataLoading) && !stats;
  const isStatsDisabled =
    (loading || dataLoading) && netSummaryItems.length > 0;

  const onDataRowClicked = (event, item) => {
    navigate("/claims/details", {
      state: {
        currentItem: item,
        claimsListReturnState: {
          claimsFilters: getCleanClaimsFilters(),
          activeTab,
        },
      },
    });
  };

  const onPageChange = async (currentPage) => {
    const filteredClaimsFilterDate = removeEmptyKeys(claimsFilters);
    const filterInput = getCleanClaimsFilters(
      filteredClaimsFilterDate,
      activeTab,
      {
        limit: claimsFilters.limit || limit || DEFAULT_LIMIT,
        page: currentPage,
      },
    );

    localStorage.setItem(
      "claimsListPagination",
      JSON.stringify({
        pageNo: currentPage,
        limit: filterInput.limit,
      }),
    );
    localStorage.setItem("claimsFiltersSaved", JSON.stringify(filterInput));

    setPageNo(currentPage);
    setClaimsFilters(filterInput);
  };

  // Check Status
  const [
    checkReq,
    { data: checkData, loading: checkLoading, error: checkError },
  ] = useMutation(claimsGQL.CHECK_STATUS, {
    update(proxy, result) {
      onSuccessfulCheckRequestResponse(result.data);
    },
    onError(error) {
      onCancelResponseError(error.graphQLErrors[0]);
    },
  });

  const onSuccessfulCheckRequestResponse = (data) => {
    if (!data.checkStatusRequest.nphies_err) {
      toast.success("Successfully Requested!");
    } else if (data.checkStatusRequest.nphies_err) {
      setErrorData(data.checkStatusRequest.nphies_err);
      setShowErrorAlert(true);
    } else {
      toast.error(`Unknown Error`);
    }
  };

  const checkClicked = (item) => {
    toast.loading("Requesting!");
    checkReq({
      variables: {
        input: { transaction_id: item.transaction_id, request_name: "claims" },
      },
    });
  };

  // Cancel Request
  const [
    cancelReq,
    { data: cancelData, loading: cancelLoading, error: cancelError },
  ] = useMutation(gqlPreAuth.CANCEL_REQUEST, {
    update(proxy, result) {
      onSuccessfulCancelRequestResponse(result.data);
    },
    onError(error) {
      onCancelResponseError(error.graphQLErrors[0]);
    },
  });

  const onSuccessfulCancelRequestResponse = (data) => {
    if (data.cancelRequest) {
      toast.success("Successfully Cancelled!");
    }
  };

  const onCancelResponseError = (error) => {
    toast.error(`${error.message}`);
  };

  const refetchCurrentClaims = async () => {
    const filterInput = getCleanClaimsFilters(claimsFilters, activeTab, {
      page: claimsFilters.page || pageNo || 1,
      limit: claimsFilters.limit || limit || DEFAULT_LIMIT,
    });

    try {
      setLoading(true);
      await refetch({ input: filterInput }).then(({ data }) => {
        setAllClaims(data?.listClaimTransactions?.data);
        setClaimsPages(data?.listClaimTransactions?.pages);
        setClaimsTotal(data?.listClaimTransactions?.total);
        setStats(data?.listClaimTransactions?.stats || null);
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelClicked = async () => {
    cancelReq({
      variables: {
        input: {
          transaction_id: selectedItem?.transaction_id,
          request_name: "claims",
        },
      },
    }).then(async (res) => {
      await refetchCurrentClaims();
    });
    setSeelectedItem(null);
    setShowConfirmModal(false);
  };

  const [submitClaimReq, { loading: submitClaimLoading }] = useMutation(
    claimsGQL.SUBMIT_CLAIMS,
    {
      onError(error) {
        toast.dismiss();
        toast.error(
          error?.graphQLErrors?.[0]?.message ||
            error?.message ||
            "Something went wrong.",
        );
      },
    },
  );

  const canSubmitClaim = (row) => {
    const status = String(row?.status || "").toLowerCase();
    const outcome = String(row?.outcome || "").toLowerCase();
    const blockedStatuses = [
      "queued",
      "pended",
      "completed",
      "complete",
      "complted",
    ];

    if (blockedStatuses.includes(status)) {
      return false;
    }

    return (
      status === "pending" ||
      outcome === "pending" ||
      status.includes("error") ||
      outcome.includes("error") ||
      outcome === "abnormal request"
    );
  };

  const submitClicked = async () => {
    if (!selectedItem?.transaction_id) {
      toast.error(t("Transaction ID is missing."));
      return;
    }

    toast.loading(t("Requesting!"));

    try {
      const previousMonthBatchDates = getPreviousMonthBatchDates()
      const res = await submitClaimReq({
        variables: {
          input: {
            id: [selectedItem.transaction_id],
            is_batch: true,
            ...previousMonthBatchDates
          }
        }
      })

      toast.dismiss();

      if (res?.data?.submitClaims?.nphies_err?.length) {
        setErrorData(res.data.submitClaims.nphies_err);
        setShowErrorAlert(true);
      } else {
        toast.success(t("Successfully Requested!"));
      }

      await refetchCurrentClaims();
      setSeelectedItem(null);
      setShowSubmitConfirmModal(false);
    } catch (error) {
      toast.dismiss();
    }
  };

  const {
    loading: pollLoading,
    error: pollError,
    data: pollData,
    refetch: refetchPoll,
  } = useQuery(claimsGQL.CLAIMS_POLL_REQ, { skip: true });

  const handleTabClick = async (tabName) => {
    setActiveTab(tabName);
    setPageNo(1);

    const nextLimit = claimsFilters.limit || limit || DEFAULT_LIMIT;
    localStorage.setItem(
      "claimsListPagination",
      JSON.stringify({
        pageNo: 1,
        limit: nextLimit,
      }),
    );

    const filterInput = getCleanClaimsFilters(claimsFilters, tabName, {
      limit: nextLimit,
      page: 1,
    });
    setClaimsFilters(filterInput);
    sessionStorage.setItem("claimsFiltersSaved", JSON.stringify(filterInput));
    localStorage.setItem("claimsFiltersSaved", JSON.stringify(filterInput));
  };

  const StyledMenu = styled((props) => (
    <Menu
      elevation={0}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      {...props}
    />
  ))(({ theme }) => ({
    "& .MuiPaper-root": {
      borderRadius: 6,
      marginTop: theme.spacing(1),
      minWidth: 180,
      color:
        theme.palette.mode === "light"
          ? "rgb(55, 65, 81)"
          : theme.palette.grey[300],
      boxShadow:
        "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
      "& .MuiMenu-list": { padding: "4px 0" },
      "& .MuiMenuItem-root": {
        "& .MuiSvgIcon-root": {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        "&:active": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  }));

  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);

  const tabs = [
    { label: t("All Claims"), value: "" },
    { label: t("Queued"), value: "queued" },
    { label: t("Error"), value: "error" },
    { label: t("Abnormal Request"), value: "Abnormal Request" },
    { label: t("Exception Error"), value: "Exception Error" },
    { label: t("Validation Error"), value: "Validation Error" },
    { label: t("Complete"), value: "complete" },
  ];

  const columns = [
    // {
    //   header: t("SQ #"),
    //   render: (row, index) =>
    //     claimsFilters?.page * claimsFilters?.limit +
    //     index +
    //     1 -
    //     claimsFilters?.limit,
    // },
    {
      header: t("Outcome / Status"),
      width: "1%",
      render: (row) => {
        return (
          <div className="flex flex-col gap-1 w-max">
            <div className="flex items-center gap-1 w-max">
              <StatusOutcomePill value={row?.outcome} variant="icon" />
              <StatusOutcomePill value={row?.status} />
            </div>
            {!row?.is_valid && (
              <span className="bg-red-50 text-red-600 text-center text-[11px] px-1 rounded border border-red-200 font-bold uppercase py-0.5">
                {t("INVALID")}
              </span>
            )}
          </div>
        );
      },
    },
    // {
    //   header: t("Errors/Issues"),
    //   render: (row) => {
    //     const hasErrors = row.errors && row.errors.length > 0;
    //     const hasDisposition = !!row.disposition;
    //     if (!hasErrors && !hasDisposition) return "---";
    //     return (
    //       <Tooltip
    //         arrow
    //         title={
    //           <div className="p-2 max-w-xs">
    //             {hasDisposition && (
    //               <div className="mb-2">
    //                 <span className="font-bold text-yellow-400">
    //                   Disposition:
    //                 </span>
    //                 <p className="text-xs">{row.disposition}</p>
    //               </div>
    //             )}
    //             {hasErrors && (
    //               <div>
    //                 <span className="font-bold text-red-400">
    //                   Technical Errors:
    //                 </span>
    //                 {row.errors.map((err, i) => (
    //                   <p
    //                     key={i}
    //                     className="text-[11px] border-b border-gray-600 py-1 last:border-0"
    //                   >
    //                     {err.error_code}: {err.error_message}
    //                   </p>
    //                 ))}
    //               </div>
    //             )}
    //           </div>
    //         }
    //       >
    //         <div className="flex items-center gap-1 text-red-500 cursor-help">
    //           <span className="text-[10px] font-bold underline decoration-dotted uppercase">
    //             {t("View Details")}
    //           </span>
    //         </div>
    //       </Tooltip>
    //     );
    //   },
    // },
    // --- Updated Status Column ---
    // {
    //   header: t('Duplicate'),
    //   render: row =>
    //     row?.is_duplicate ? (
    //       <Tooltip title={t('Click to view original claim')}>
    //         <span
    //           className=' cursor-pointer underline text-xs font-medium'
    //           onClick={e => {
    //             e.stopPropagation()
    //             navigate(`/claims/details/${row.duplicate_ref}`)
    //           }}
    //         >
    //           {row.duplicate_ref}
    //         </span>
    //       </Tooltip>
    //     ) : (
    //       <span className='text-gray-400 text-xs'>---</span>
    //     )
    // },
    {
      header: t("Patient"),
      render: (row) => (
        <div className="flex flex-col min-w-[200px] py-1">
          <span className="font-bold text-gray-900 text-[12.5px] capitalize leading-snug">
            {row?.patient_name || "---"}
          </span>
          <div className="text-[11px] text-gray-500 font-medium mt-0.5">
            Doc ID: {row?.document_id || "---"}
          </div>
        </div>
      ),
    },
    {
      header: t("Payer"),
      render: (row) => (
        <div className="flex items-start gap-2 min-w-[220px] py-1 whitespace-pre-wrap">
          {/* <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#0070c2"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 mt-0.5"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            <path d="M9 12l2 2 4-4"></path>
          </svg> */}
          <div className="flex flex-col">
            <span className="font-medium text-gray-800 text-[12.5px] leading-snug">
              {row?.payer_name || row?.tpa_name || "---"}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: t("Net"),
      align: "right",
      render: (row) => (
        <span className="font-semibold text-[#0070C2] whitespace-nowrap">
          {formatAmount(row?.total_net)}
        </span>
      ),
    },
    // { header: t('Batch ID'), field: 'batch_id' },
    // {
    //   header: t('Referral'),
    //   align: 'center',
    //   render: row => <ReferralFlag item={row} />
    // },
    {
      header: t("Date Ordered"),
      render: (row) => FormatDate(row?.date_ordered),
    },
    { header: t("Type"), field: "type" },
    { header: t("Episode No."), field: "episode_no" },
    { header: t("Batch ID."), field: "batch_id" },
  ];

  return (
    <>
      <div className="pre_auth_container !p-0">
        {/* Header Buttons */}
        <div className="flex justify-between flex-col sm:flex-row gap-2 mb-2">
          <div className="w-full sm:w-[calc(100%-150px)]">
            <span className="font-semibold text-black/70 text-lg ">
              {t("CLAIMS TRANSACTION")}
              <span className=" text-lg ml-1">
                ({data?.listClaimTransactions?.total || 0}{" "}
                <span className="text-xs font-bold italic">{t("Entries")}</span>
                )
              </span>
            </span>
          </div>
          <div className="flex w-full gap-1.5 justify-end flex-col sm:flex-row">
            {/* <AppButton
              label={t('Bulk Cancel')}
              variant='outlined'
              onClick={() => setShowBulkCancelModal(true)}
              startIcon={<CancelOutlinedIcon sx={{ fontSize: 17 }} />}
              className='!border-primary !text-primary hover:!bg-primary/10'
            />
            <AppButton
              label={t('Bulk Ready')}
              variant='outlined'
              onClick={() => setShowBulkReadyModal(true)}
              startIcon={<TaskAltIcon sx={{ fontSize: 17 }} />}
              className='!border-primary !text-primary hover:!bg-primary/10'
            /> */}
            <AppButton
              disabled={!stats}
              label={t("Show Errors")}
              variant="outlined"
              onClick={() => setShowErrorModal(true)}
              startIcon={<PollIcon />}
              className="!border-primary !text-primary hover:!bg-primary/10"
            />
            <AppButton
              label={t("Invalid ICD Code")}
              variant="outlined"
              onClick={() => setShowInvalidICDModal(true)}
              startIcon={
                <ReportProblemOutlinedIcon
                  sx={{ fontSize: 17, color: "#e11d48" }}
                />
              }
              className="!border-rose-300 !text-rose-700 !bg-rose-50/80 hover:!bg-rose-100 !font-semibold"
            />
            <AppButton
              label={t("Item Issue Codes")}
              variant="outlined"
              onClick={() => setShowClaimItemIssueModal(true)}
              startIcon={
                <ReportProblemOutlinedIcon
                  sx={{ fontSize: 17, color: "#e11d48" }}
                />
              }
              className="!border-rose-300 !text-rose-700 !bg-rose-50/80 hover:!bg-rose-100 !font-semibold"
            />
            {/* <AppButton
              label={t('Referral Bulk Update')}
              variant='outlined'
              onClick={() => navigate('/claims/referral-bulk-update')}
              startIcon={<PublishedWithChangesOutlinedIcon sx={{ fontSize: 17 }} />}
              className='!border-primary !text-primary hover:!bg-primary/10'
            /> */}
            {/* <AppButton
              label={t('Item Bulk Replace')}
              variant='outlined'
              onClick={() => navigate('/claims/item-bulk-replace')}
              startIcon={<PublishedWithChangesOutlinedIcon sx={{ fontSize: 17 }} />}
              className='!border-primary !text-primary hover:!bg-primary/10'
            /> */}
            <AppButton
              label={t('AI Agent')}
              onClick={() => navigate('/claims/ai-agent')}
              startIcon={<AutoModeIcon />}
              variant='outlined'
              className='!border-primary !text-primary hover:!bg-primary/10'
            />
            <AppButton
              label={t('Request Claim')}
              onClick={() => navigate('/claims/form')}
              startIcon={<AddIcon />}
            />
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
            <Filter
              activeTab={activeTab}
              claimsFilters={claimsFilters}
              setClaimsFilters={setClaimsFilters}
              refetch={refetch}
              setLoading={setLoading}
              setPageNo={setPageNo}
              fromDate={fromDate}
              toDate={toDate}
            />
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 mb-2">
          {netSummaryItems?.map((item) => (
            <div
              key={item?.label}
              className={`rounded-md bg-white p-2 shadow-sm transition-opacity ${
                showStatsSkeleton || !netSummaryItems.length
                  ? "opacity-50 pointer-events-none"
                  : ""
              }`}
            >
              <div className="text-[10px] font-black uppercase truncate text-slate-500">
                {item.label}
              </div>
              <div
                className={`text-[13px] font-black mt-0.5 ${item?.textClassName}`}
              >
                {formatAmount(item?.value)}
              </div>
            </div>
          ))}
        </div>

        <AppModal
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title={t("Claims Error Breakdown")}
          maxWidth="xl"
          fullWidth={true}
          contentSx={{ overflow: "hidden" }}
        >
          {(() => {
            const getPayerName = (payerId) => {
              if (!payerId) return t("All Payers");
              const payersData =
                JSON.parse(localStorage.getItem("payersData")) || [];
              for (const category of payersData) {
                const found = category.payers.find((p) => p.id === payerId);
                if (found) return found.name_en;
              }
              return payerId;
            };

            const getTpaName = (tpaId) => {
              if (!tpaId) return "";
              const payersData =
                JSON.parse(localStorage.getItem("payersData")) || [];
              return payersData.find((category) => category.id === tpaId)?.name_en || tpaId;
            };

            const validationErrors = stats?.validation_error || [];
            const isFewValidationErrors = validationErrors.length < 4;
            const leftColSpan = isFewValidationErrors
              ? "md:col-span-2"
              : "md:col-span-3";
            const rightColSpan = isFewValidationErrors
              ? "md:col-span-2"
              : "md:col-span-1";
            const groupErrorsByDisposition = (errorList) => {
              return errorList.reduce((groups, errorItem) => {
                const dispositionValue = errorItem?.disposition?.trim();
                const disposition = dispositionValue || t("No Disposition");
                const existingGroup = groups.find(
                  (group) => group.disposition === disposition,
                );

                if (existingGroup) {
                  existingGroup.errors.push(errorItem);
                  existingGroup.count += errorItem.count || 0;
                  existingGroup.totalNet += errorItem.total_net || 0;
                  return groups;
                }

                return [
                  ...groups,
                  {
                    disposition,
                    dispositionValue,
                    count: errorItem.count || 0,
                    totalNet: errorItem.total_net || 0,
                    errors: [errorItem],
                  },
                ];
              }, []);
            };
            return (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-[13px] border-b pb-2 mb-1">
                  {claimsFilters.tpa_id && (
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-500 uppercase tracking-tight">
                        {t("TPA")}:
                      </span>
                      <span className="font-semibold text-[#0070C2]">
                        {getTpaName(claimsFilters.tpa_id)}
                      </span>
                    </div>
                  )}
                  {claimsFilters.payer_id && (
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-500 uppercase tracking-tight">
                        {t("Payer")}:
                      </span>
                      <span className="font-semibold text-[#0070C2]">
                        {getPayerName(claimsFilters.payer_id)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-x-6 ml-auto">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-500 uppercase tracking-tight">
                        {t("From Date")}:
                      </span>
                      <span className="font-semibold text-[#0070C2]">
                        {claimsFilters.from_date
                          ? FormatDate(claimsFilters.from_date)
                          : "---"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-500 uppercase tracking-tight">
                        {t("To Date")}:
                      </span>
                      <span className="font-semibold text-[#0070C2]">
                        {claimsFilters.to_date
                          ? FormatDate(claimsFilters.to_date)
                          : "---"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  {/* Column 1: Validation Errors (Big) */}
                  <div className="md:col-span-2">
                    {[
                      {
                        label: t("Validation Errors"),
                        key: "validation_error",
                        color: "bg-amber-50 text-amber-600 border-amber-100",
                        scrollbar: "#d97706",
                      },
                    ].map((statInfo) => {
                      const errorList = stats?.[statInfo.key] || [];
                      const totalCount = errorList.reduce(
                        (acc, curr) => acc + curr.count,
                        0,
                      );
                      return (
                        <div
                          key={statInfo.key}
                          className={`border rounded-lg p-2 flex flex-col h-full ${statInfo.color}`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-black uppercase tracking-wider">
                              {statInfo.label}
                            </span>
                            <span className="text-sm font-black">
                              {totalCount}
                            </span>
                          </div>
                          <Box
                            className="flex-grow max-h-[350px] overflow-y-auto space-y-2 custom-scrollbar pr-1"
                            sx={{
                              "&::-webkit-scrollbar": { width: "4px" },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: statInfo.scrollbar,
                                borderRadius: "10px",
                              },
                            }}
                          >
                            {errorList.length > 0 ? (
                              errorList.map((err, idx) => (
                                <div
                                  key={idx}
                                  className="text-[11px] bg-white/70 p-1 rounded-lg border border-current/10 cursor-pointer hover:bg-white transition-all duration-200"
                                  onClick={() =>
                                    handleStatErrorClick(statInfo.key, err)
                                  }
                                >
                                  <div className="flex justify-between items-center gap-4">
                                    <span className="font-bold flex-shrink-0 bg-current/10 px-2 py-1 rounded">
                                      {err.error_code}
                                    </span>
                                    <span className="flex-grow font-medium leading-tight">
                                      {err.error_message}
                                    </span>
                                    <span className="font-black bg-current/20 px-2 py-0.5 rounded-full text-[11px]">
                                      {err.count}
                                    </span>
                                  </div>
                                  <div className="mt-1 text-[10px] font-bold opacity-80 text-right">
                                    {t("Net")}: {formatAmount(err.total_net)}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm opacity-60 text-center py-10">
                                {t("No validation issues found")}
                              </div>
                            )}
                          </Box>
                        </div>
                      );
                    })}
                  </div>

                  {/* Column 2: General Errors */}
                  <div className="md:col-span-1">
                    {[
                      {
                        label: t("General Errors"),
                        key: "error",
                        color: "bg-red-50 text-red-600 border-red-100",
                        scrollbar: "#dc2626",
                      },
                    ].map((statInfo) => {
                      const errorList = stats?.[statInfo.key] || [];
                      const totalCount = errorList.reduce(
                        (acc, curr) => acc + curr.count,
                        0,
                      );
                      return (
                        <div
                          key={statInfo.key}
                          className={`border rounded-lg p-2 flex flex-col h-full ${statInfo.color}`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-[11px] font-bold uppercase truncate">
                              {statInfo.label}
                            </span>
                            <span className="text-sm font-black">
                              {totalCount}
                            </span>
                          </div>
                          <Box
                            className="flex-grow max-h-[400px] overflow-y-auto space-y-1.5 custom-scrollbar pr-1"
                            sx={{
                              "&::-webkit-scrollbar": { width: "4px" },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: statInfo.scrollbar,
                                borderRadius: "10px",
                              },
                            }}
                          >
                            {errorList.length > 0 ? (
                              groupErrorsByDisposition(errorList).map(
                                (group) => (
                                  <div
                                    key={group.disposition}
                                    className={`rounded border border-current/10 bg-white/40 p-1.5 transition-colors ${
                                      group.dispositionValue
                                        ? "cursor-pointer hover:bg-white"
                                        : "cursor-default"
                                    }`}
                                    onClick={() =>
                                      handleDispositionGroupClick(group)
                                    }
                                  >
                                    <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
                                      <span className="font-black uppercase tracking-wide truncate">
                                        {group.disposition}
                                      </span>
                                      <span className="font-black bg-current/15 px-2 py-0.5 rounded-full">
                                        {group.count}
                                      </span>
                                    </div>
                                    <div className="mb-1 text-[10px] font-bold opacity-80 text-right">
                                      {t("Net")}: {formatAmount(group.totalNet)}
                                    </div>
                                    <div className="space-y-1">
                                      {group.errors.map((err, idx) => (
                                        <div
                                          key={`${group.disposition}-${err.error_code}-${idx}`}
                                          className="text-[11px] bg-white/60 p-1.5 rounded border border-current/5 shadow-sm"
                                        >
                                          <div className="flex justify-between items-start gap-1">
                                            <span className="font-bold whitespace-nowrap opacity-90">
                                              {err.error_code}:
                                            </span>
                                            <span className="truncate opacity-80">
                                              {err.error_message}
                                            </span>
                                            <span className="font-black ml-auto">
                                              {err.count}
                                            </span>
                                          </div>
                                          <div className="mt-1 text-[10px] font-bold opacity-80 text-right">
                                            {t("Net")}:{" "}
                                            {formatAmount(err.total_net)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ),
                              )
                            ) : (
                              <div className="text-[10px] opacity-60 text-center py-1">
                                {t("No issues")}
                              </div>
                            )}
                          </Box>
                        </div>
                      );
                    })}
                  </div>

                  {/* Column 3: Others (Abnormal + Exception) */}
                  <div className="md:col-span-1 flex flex-col gap-2">
                    {[
                      {
                        label: t("Abnormal Requests"),
                        key: "abnormal",
                        color: "bg-orange-50 text-orange-600 border-orange-100",
                        scrollbar: "#ea580c",
                      },
                      {
                        label: t("Exception Errors"),
                        key: "exception_error",
                        color: "bg-purple-50 text-purple-600 border-purple-100",
                        scrollbar: "#9333ea",
                      },
                    ].map((statInfo) => {
                      const errorList = stats?.[statInfo.key] || [];
                      const totalCount = errorList.reduce(
                        (acc, curr) => acc + curr.count,
                        0,
                      );
                      return (
                        <div
                          key={statInfo.key}
                          className={`border rounded-lg p-2 flex flex-col flex-1 ${statInfo.color}`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-[11px] font-bold uppercase truncate">
                              {statInfo.label}
                            </span>
                            <span className="text-sm font-black">
                              {totalCount}
                            </span>
                          </div>
                          <Box
                            className="max-h-[160px] overflow-y-auto space-y-1.5 custom-scrollbar pr-1"
                            sx={{
                              "&::-webkit-scrollbar": { width: "4px" },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: statInfo.scrollbar,
                                borderRadius: "10px",
                              },
                            }}
                          >
                            {errorList.length > 0 ? (
                              errorList.map((err, idx) => (
                                <div
                                  key={idx}
                                  className="text-[11px] bg-white/50 p-1.5 rounded border border-current/5 cursor-pointer hover:bg-white transition-colors shadow-sm"
                                  onClick={() =>
                                    handleStatErrorClick(statInfo.key, err)
                                  }
                                >
                                  <div className="flex justify-between items-start gap-1">
                                    <span className="font-bold whitespace-nowrap opacity-90">
                                      {err.error_code}:
                                    </span>
                                    <span className="truncate opacity-80">
                                      {err.error_message}
                                    </span>
                                    <span className="font-black ml-auto">
                                      {err.count}
                                    </span>
                                  </div>
                                  <div className="mt-1 text-[10px] font-bold opacity-80 text-right">
                                    {t("Net")}: {formatAmount(err.total_net)}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-[10px] opacity-60 text-center py-1">
                                {t("No issues")}
                              </div>
                            )}
                          </Box>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </AppModal>

        {/* Tabs */}
        <div className="my-2">
          <ul className="flex flex-wrap text-center text-gray-500 bg-gray-100 rounded-lg p-1 shadow-inner gap-0.5">
            {tabs.map((tab) => (
              <li key={tab.value} className="flex-1">
                <a
                  className={`flex justify-center py-0.5 cursor-pointer text-[13px] rounded-lg transition-colors hover:text-[#0070C2] ${
                    activeTab === tab.value ? "bg-[#0070C2] !text-white" : ""
                  }`}
                  onClick={() => handleTabClick(tab.value)}
                >
                  {tab.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Table */}
        <div>
          <AppTable
            cellClassName="!py-0"
            actioncellClassName="!py-0"
            columns={columns}
            data={allClaims}
            loading={loading || dataLoading}
            bodyRowPadding="7px"
            onView={(row) => onDataRowClicked(null, row)}
            canEditRow={(row) => canEditClaimTransaction(row?.status)}
            onEdit={(row) =>
              navigate("/claims/edit/form/" + row.transaction_id, {
                state: { returnTo: "/claims" },
              })
            }
            onJsonView={(row) => {
              setSeelectedItem(row);
              setOpen(true);
            }}
            onCheck={(row) => checkClicked(row)}
            onCancel={(row) => {
              setSeelectedItem(row);
              setShowConfirmModal(true);
            }}
            onCommunication={(row) => onDataRowClicked(null, row)}
            /* Follow Up action temporarily disabled.
            onFollowUp={(row) =>
              navigate(`/claims/edit/form/${row?.transaction_id}/follow-up`, {
                state: { returnTo: "/claims" },
              })
            } 
              */
            customActions={[
              {
                title: "Submit",
                icon: <SendOutlinedIcon sx={{ fontSize: 19 }} />,
                shouldRender: canSubmitClaim,
                className:
                  "cursor-pointer flex items-center justify-center text-emerald-600 hover:text-emerald-700 transition-colors",
                onClick: (row) => {
                  setSeelectedItem(row);
                  setShowSubmitConfirmModal(true);
                },
              },
            ]}
            LoaderComponent={
              <TableSkeletonLoader
                columnCount={columns.length + 1}
                rowCount={10}
              />
            }
          />
        </div>

        {/* Pagination */}
        <div>
          <Pagination
            currentPage={claimsFilters?.page}
            totalPages={claimsPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <AppErrorAlert data={errorData} />

      <ConformationModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        onDelete={() => cancelClicked()}
        text={"Cancel"}
      />
      <ConformationModal
        open={showSubmitConfirmModal}
        setOpen={setShowSubmitConfirmModal}
        onDelete={() => submitClicked()}
        text={"Submit"}
        isLoading={submitClaimLoading}
      />
      <ClaimJsonViewModal
        setOpen={setOpen}
        open={open}
        handleClose={handleClose}
        selectedItem={selectedItem}
      />
      <ClaimReportModal
        open={reportOpen}
        setOpen={setReportOpen}
        handleClose={handleReportClose}
        handleClickOpen={handleReportOpen}
      />
      <BulkCancelBundleModal
        open={showBulkCancelModal}
        action="cancel"
        onClose={() => setShowBulkCancelModal(false)}
        onCompleted={() => refetch({ input: getCleanClaimsFilters() })}
      />
      <BulkCancelBundleModal
        open={showBulkReadyModal}
        action="ready"
        onClose={() => setShowBulkReadyModal(false)}
        onCompleted={() => refetch({ input: getCleanClaimsFilters() })}
      />
      <InvalidICDCodeModal
        open={showInvalidICDModal}
        onClose={() => setShowInvalidICDModal(false)}
        initialFromDate={claimsFilters?.from_date || fromDate}
        initialToDate={claimsFilters?.to_date || toDate}
        initialErrorCode="IB-00242"
      />
      <ClaimItemIssueCodesModal
        open={showClaimItemIssueModal}
        onClose={() => setShowClaimItemIssueModal(false)}
        initialFromDate={claimsFilters?.from_date || fromDate}
        initialToDate={claimsFilters?.to_date || toDate}
        initialErrorCode="IB-00238"
      />
    </>
  );
}

export default ClaimsList;
